import React, { useEffect, useMemo, useState } from "react";
import SnButton from "@shared/components/SnButton";
import {
  onTranscriptView,
  requestMeetingAudioSeek,
} from "@shared/libs/meetingAudioStore";

/**
 * 목적: 전사 원문 보기 칸 — 시각 줄을 구분해 보여 주고, 시각을 누르면 같은 행의 녹음기가 그 시점부터 재생한다 (2026-10-23).
 *
 * 호출 예:
 * <SnTranscriptView text={formData.dc_stt} rowKey={formData.KeyField} editor={<SnMemoBox ... />} />
 *
 * 주요 기능:
 * - 보기(기본) : "[HH:MM:SS]" 로 시작하는 줄을 시각으로 보고 파란 글자로, 덩어리 사이를 띄워 보인다. 글은 고칠 수 없다
 * - 편집 : 넘겨받은 편집 칸(메모 칸 그대로)을 보인다. 입력 · 저장 동작은 메모 칸과 같다
 * - 다른 행으로 옮기거나 화면이 새로 전사하면(requestTranscriptView) 보기로 돌아간다
 * - "※" 로 시작하는 줄(중앙 누락 확인 — 받아쓴 글이 짧은 구간)은 주황색
 *
 * 사용처:
 * SnFindFormContols 의 메모 칸(SnMemoBox) 중 cd_value = 'TRANSCRIPT' 인 칸 — AI 회의록 전사 원문(dc_stt).
 *
 * @param text     전사 원문
 * @param rowKey   행 키(KeyField). 녹음기와 같은 값이어야 그 행의 녹음기가 재생한다
 * @param editor   편집 모드에 보일 칸
 * @param readOnly 고칠 수 없는 칸이면 [편집] 을 감춘다
 */
interface SnTranscriptViewProps {
  text: string;
  rowKey: string;
  editor: React.ReactNode;
  readOnly?: boolean;
}

interface TranscriptBlock {
  seconds: number | null;
  label: string;
  lines: string[];
}

// "[00:16:02]" 또는 "[00:16:02] 말" — 앞 것은 덩어리 시각 줄, 뒤 것은 말 토막마다 시각이 붙는 모델의 줄
const TIME_LINE = /^\[(\d{1,2}):(\d{2}):(\d{2})\]\s*(.*)$/;

const parseTranscript = (text: string): TranscriptBlock[] => {
  const blocks: TranscriptBlock[] = [];
  let current: TranscriptBlock | null = null;
  for (const raw of String(text || "").split(/\r?\n/)) {
    const line = raw.trimEnd();
    const match = line.match(TIME_LINE);
    if (match) {
      current = {
        seconds: Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]),
        label: `${match[1].padStart(2, "0")}:${match[2]}:${match[3]}`,
        lines: match[4] ? [match[4]] : [],
      };
      blocks.push(current);
      continue;
    }
    if (!line.trim()) continue;
    if (!current) {
      // 시각 없는 앞부분(예전 전사 · 손으로 친 글)
      current = { seconds: null, label: "", lines: [] };
      blocks.push(current);
    }
    current.lines.push(line);
  }
  return blocks;
};

const SnTranscriptView = ({ text, rowKey, editor, readOnly = false }: SnTranscriptViewProps) => {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const blocks = useMemo(() => parseTranscript(text), [text]);

  // 다른 행으로 옮기면 보기로
  useEffect(() => {
    setMode("view");
  }, [rowKey]);

  // 화면이 새로 전사하면 보기로
  useEffect(
    () =>
      onTranscriptView((key) => {
        if (key === rowKey) setMode("view");
      }),
    [rowKey]
  );

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", minHeight: 0 }}>
      {!readOnly && (
        <div style={{ position: "absolute", top: 4, right: 8, zIndex: 1 }}>
          <SnButton
            text={mode === "view" ? "편집" : "보기"}
            onClick={() => setMode(mode === "view" ? "edit" : "view")}
          />
        </div>
      )}
      {mode === "edit" ? (
        editor
      ) : (
        <div
          style={{
            height: "100%",
            overflowY: "auto",
            border: "1px solid rgb(186,191,199)",
            borderRadius: 4,
            padding: "8px 96px 8px 10px",
            fontSize: 13,
            lineHeight: 1.6,
            background: "#fff",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {blocks.length === 0 && <span style={{ color: "#9ca3af" }}>전사 원문이 없습니다.</span>}
          {blocks.map((block, index) => (
            <div key={index} style={{ marginBottom: 12 }}>
              {block.seconds != null && (
                <div>
                  <span
                    role="button"
                    tabIndex={0}
                    title="재생"
                    onClick={() => requestMeetingAudioSeek(rowKey, block.seconds as number)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") requestMeetingAudioSeek(rowKey, block.seconds as number);
                    }}
                    style={{ color: "#1d4ed8", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                  >
                    [{block.label}]
                  </span>
                </div>
              )}
              {block.lines.map((line, lineIndex) => (
                // "※ …" 는 중앙 누락 확인 줄 — 받아쓴 글이 말한 시간에 비해 짧은 구간(바로 위 시각을 눌러 들어 본다)
                <div
                  key={lineIndex}
                  style={line.startsWith("※") ? { color: "#c2410c", fontWeight: 600 } : undefined}
                >
                  {line}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(SnTranscriptView);
